import { Injectable, signal, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { User } from '../types/user';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { LoginResponse } from '../shared/models/LoginResponse';
import { TranslationStorageService } from './TranslationStorage.service';
import { RequestType } from '../shared/enums/requestType';
import { GlobalService } from './global.service';
import { environment } from '../../../environments/environment';





@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private router = inject(Router);
  private http = inject(HttpClient);
  private translationStorage = inject(TranslationStorageService);
  private globalService = inject(GlobalService);

  private currentUserSignal = signal<User | null>(null);
  private tokenKey = 'auth_token';
  isLogin: boolean = false;
  public isAuthenticated$ = new BehaviorSubject<boolean>(false);

  private inactivityTimeout = 5 * 60 * 1000; // 5 minutes
  private activityTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly activityEvents = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];

  private apiUrl = environment.apiUrl + '/auth';
  private apiUrlColumns = environment.apiUrl;

  /** Every key that holds session data or personal data; all are removed on logout. */
  private static readonly SESSION_KEYS = [
    'auth_token',
    'currentUser',
    'token',
    'identUnique',
    'cin',
    'ehoueya_id_token',
    'ehoweya_state',
    'code_verifier',
    'codeVerifier',
    'nom',
    'prenom',
    'username',
    'lib_gouv',
    'lib_del',
    'cinCitoyen',
    'idProgram',
    'serviceName'
  ];

  constructor() {
    const storedUser = sessionStorage.getItem('currentUser');
    const token = sessionStorage.getItem(this.tokenKey);

    if (token && !this.isTokenExpired(token)) {
      if (storedUser) {
        this.currentUserSignal.set(JSON.parse(storedUser));
      }
      this.isAuthenticated$.next(true);
      this.startInactivityWatcher();
    } else if (token || storedUser) {
      this.clearSession();
    }
  }

  // =========================
  // 🔹 USER STATE
  // =========================

  public get currentUserValue(): User | null {
    return this.currentUserSignal();
  }

  setCurrentUser(user: User) {
    this.currentUserSignal.set(user);
    const safeUser = JSON.parse(JSON.stringify(user));
    if (safeUser?.user) {
      delete safeUser.user.password;
    }
    sessionStorage.setItem('currentUser', JSON.stringify(safeUser));
  }

  clearSession() {
    console.trace('[DEBUG clearSession] appelé depuis :');
    for (const key of AuthenticationService.SESSION_KEYS) {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    }

    this.stopInactivityWatcher();
    this.currentUserSignal.set(null);
    this.isAuthenticated$.next(false);
  }

  signup(signupRequest: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/sign-up`, signupRequest);
  }

  getEhoweyaConfig(): Observable<any> {
    return this.http.get<any>(`${this.globalService.BASE_OPEN_API_URL}auth/ehouweya-config`);
  }

  getUserInfo(code: string, codeVerifier: string): Observable<any> {
    return this.http.get<any>(
      `${this.globalService.BASE_OPEN_API_URL}auth/get-ehoweya-user`,
      {
        params: { code, codeVerifier }
      }
    );
  }

  registerUserFromEhoweya(ehoweyaUser: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/register-from-ehoweya`, ehoweyaUser).pipe(
      tap((res) => {
        if (res?.token) {
          this.setSession(res.token, res.user);
        }
      })
    );
  }

  // verifyUserAndSendOtp(loginRequest: any, turnstileToken?: string): Observable<any> {
  //   const headers = turnstileToken
  //     ? new HttpHeaders({ [X-Turnstile-Token]: turnstileToken })
  //     : new HttpHeaders();
  //   return this.http.post<any>(`${this.apiUrl}/verify-user`, loginRequest, { headers });
  // }
  verifyUserAndSendOtp(loginRequest: any, turnstileToken?: string): Observable<any> {
    if (turnstileToken) {
      const headers = new HttpHeaders({ 'X-Turnstile-Token': turnstileToken });
      return this.http.post<LoginResponse>(`${this.apiUrl}/verify-user-captcha`, loginRequest, { headers });
    }
    return this.http.post<LoginResponse>(`${this.apiUrl}/verify-user`, loginRequest);
  }

  verifyOtp(phoneNumber: string, otp: string, cin: string, dateNaiss: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.apiUrl}/verify-otp`, {
        numTel: phoneNumber,
        otp: otp,
        identificationNumber: cin,
        dateNaissance: dateNaiss
      })
      .pipe(
        tap((res) => {
          console.log('[DEBUG verify-otp] keys=', Object.keys(res ?? {}), 'hasToken=', !!res?.token);
          if (res?.token) {
            this.setSession(res.token);
          }
          console.log('[DEBUG verify-otp] auth_token after setSession=', !!sessionStorage.getItem(this.tokenKey));
        })
      );
  }

  resendOtp(phoneNumber: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/resend-otp`, {
      numTel: phoneNumber
    });
  }


  getTrabslatedColumns() {
    const url = this.apiUrlColumns + '/services/get-column-names'
    return this.globalService.call(RequestType.GET, url)
      .subscribe((columns) => {
        this.translationStorage.saveColumns(columns);
      });
  }
  logout() {
    const idToken = sessionStorage.getItem('ehoueya_id_token') || this.decodeEhoweyaIdToken();
    this.clearSession();

    if (idToken) {
      this.performEhoweyaLogout(idToken);
    } else {
      this.redirectToLogin();
    }
  }

  /** Navigue vers /login. Si l'on y est déjà, le routeur ne recrée pas l'écran : on recharge la page. */
  private redirectToLogin() {
    if (this.router.url.startsWith('/login')) {
      window.location.assign('/login');
    } else {
      this.router.navigate(['/login']);
    }
  }

  getEhoweyaLogoutUrl(idToken: string): Observable<{ logout_url: string }> {
    return this.http.get<{ logout_url: string }>(`${this.globalService.BASE_OPEN_API_URL}auth/logout`, {
      params: { id_token_hint: idToken }
    });
  }

  private performEhoweyaLogout(idToken: string) {
    this.getEhoweyaLogoutUrl(idToken).subscribe({
      next: (res) => {
        if (res?.logout_url) {
          window.location.href = res.logout_url;
        } else {
          this.router.navigate(['/login']);
        }
      },
      error: () => this.router.navigate(['/login'])
    });
  }

  private decodeEhoweyaIdToken(): string | null {
    const token = sessionStorage.getItem(this.tokenKey);
    if (!token) return null;

    const decoded = this.decodeToken(token);
    return decoded?.id_token_hint || null;
  }

  setSession(token: string, user?: User) {
    sessionStorage.setItem(this.tokenKey, token);

    if (user) {
      this.setCurrentUser(user);
    }

    this.isAuthenticated$.next(true);
    this.startInactivityWatcher();
    this.getTrabslatedColumns();
  }

  isLoggedIn(): boolean {
    return this.hasValidSession();
  }

  /** True when a token is stored and has not expired. */
  hasValidSession(): boolean {
    const token = sessionStorage.getItem(this.tokenKey);
    return !!token && !this.isTokenExpired(token);
  }

  private isTokenExpired(token: string): boolean {
    const decoded = this.decodeToken(token);
    if (!decoded || typeof decoded.exp !== 'number') {
      return true;
    }
    return Date.now() >= decoded.exp * 1000;
  }


  private decodeToken(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length < 2) return null;

      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const decoded = atob(base64);
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  getIdentUni(): string {
    const token = sessionStorage.getItem(this.tokenKey);
    if (!token) return '';

    const decoded = this.decodeToken(token);
    if (!decoded) return '';

    sessionStorage.setItem('identUnique', decoded.sub);
    sessionStorage.setItem('cin', decoded.cin);

    return decoded.sub;
  }
  getEhoweyaToken(code: string, codeVerifier: string): Observable<any> {
    const params = new HttpParams()
      .set('code', code)
      .set('codeVerifier', codeVerifier);

    return this.http.get(`${environment.BACK_END_POINT}open-api/auth/get-ehoweya-user`, { params });
  }


  public get currentUserName(): string | null {
    return this.currentUserValue ? 'user' : null;
  }

  private startInactivityWatcher() {
    this.stopInactivityWatcher();
    this.activityEvents.forEach((event) =>
      document.addEventListener(event, this.resetInactivityTimer, { passive: true })
    );
    this.resetInactivityTimer();
  }

  private stopInactivityWatcher() {
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
      this.activityTimer = null;
    }
    this.activityEvents.forEach((event) =>
      document.removeEventListener(event, this.resetInactivityTimer)
    );
  }

  private resetInactivityTimer = () => {
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
    }
    this.activityTimer = setTimeout(() => {
      this.logout();
    }, this.inactivityTimeout);
  };
  refreshToken() {
    return this.http.post<{ token: string }>(`${this.apiUrl}/refresh`, {}).pipe(
      tap((res) => sessionStorage.setItem(this.tokenKey, res.token))
    );
  }
}
